import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';

import { CONTEXT } from '../models/context';
import { ConfigService } from '../services/config.service';
import { TokenService } from '../services/token.service';
import { ConfigClientService } from '../services';
import { VerificadorPreciosAuthService } from '../services/verificador-precios/verificador-precios-auth.service';

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const normalizePath = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return '/';
  }

  return `/${trimmed.replace(/^\/+/, '')}`.replace(/\/+$/, '');
};

const isApiRequest = (requestUrl: string, apiBaseUrl: string): boolean => {
  if (!apiBaseUrl) {
    return false;
  }

  if (isAbsoluteUrl(apiBaseUrl)) {
    return requestUrl.startsWith(apiBaseUrl);
  }

  if (isAbsoluteUrl(requestUrl)) {
    const url = new URL(requestUrl);
    return normalizePath(url.pathname).startsWith(normalizePath(apiBaseUrl));
  }

  return normalizePath(requestUrl).startsWith(normalizePath(apiBaseUrl));
};

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const context = request.context.get(CONTEXT);
  if (context?.noSendHeader) {
    return next(request);
  }

  const configService = inject(ConfigService);
  const configClientService = inject(ConfigClientService);
  const config = configClientService.getConfig();
  const isApiTarget =
    isApiRequest(request.url, config?.api_sinergial ?? '') ||
    isApiRequest(request.url, config?.api_integra ?? '') ||
    isApiRequest(request.url, config?.api_pagos ?? '');
  if (!isApiTarget) {
    return next(request);
  }

  const tokenService = inject(TokenService);
  const router = inject(Router);
  const verificadorAuthService = inject(VerificadorPreciosAuthService);

  const isPagosRequest = isApiRequest(request.url, config?.api_pagos ?? '');
  // El verificador de precios pega contra la MISMA api_sinergial que el usuario logueado,
  // así que no se puede distinguir por URL: la audiencia la declara el caller vía CONTEXT.
  const usaClientCredentials = context?.clientTokenAccess === true;

  let token: string | null;
  if (usaClientCredentials) {
    token = verificadorAuthService.getToken();
  } else if (isPagosRequest) {
    token = localStorage.getItem(configService.getAppConfig().api_pagos_token_storage_key);
  } else {
    token = tokenService.getAccessToken();
  }

  const headers: Record<string, string> = {
    'Accept-Language': 'es-AR',
    'X-Verificar-Permisos': context?.noVerifyPermissions ? 'false' : 'true',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return next(request.clone({ setHeaders: headers })).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        if (usaClientCredentials) {
          // Kiosco público: no hay sesión que limpiar ni /login al que redirigir.
          // Se renueva el token de aplicación y se reintenta la request original.
          verificadorAuthService.clearToken();

          return verificadorAuthService.obtenerToken().pipe(
            switchMap((nuevoToken) => {
              const retryHeaders = {
                ...headers,
                Authorization: `Bearer ${nuevoToken}`,
              };
              return next(request.clone({ setHeaders: retryHeaders }));
            }),
            catchError(() => throwError(() => error)),
          );
        }

        if (isPagosRequest) {
          const apiPagos = config?.api_pagos ?? '';
          const apiPagosWithoutVersion = apiPagos.replace(/\/v\d+$/, '');

          // Sin config todavía cargado, apiPagosWithoutVersion queda '' y la URL
          // resultante sería relativa (ej. '/auth/authenticate' contra el origin
          // de la app en vez del host de pagos). Mejor dejar pasar el 401 original.
          if (!apiPagosWithoutVersion) {
            return throwError(() => error);
          }

          const http = inject(HttpClient);
          const appConfig = configService.getAppConfig();
          const body = {
            client_id: appConfig.api_pagos_client_id,
            secret_key: config?.api_pagos_secret_key ?? '',
          };
          const noHeaderContext = new HttpContext().set(CONTEXT, { noSendHeader: true });

          return http
            .post<{ token: string }>(`${apiPagosWithoutVersion}/auth/authenticate`, body, {
              context: noHeaderContext,
            })
            .pipe(
              switchMap((response) => {
                localStorage.setItem(appConfig.api_pagos_token_storage_key, response.token);
                const retryHeaders = {
                  ...headers,
                  Authorization: `Bearer ${response.token}`,
                };
                return next(request.clone({ setHeaders: retryHeaders }));
              }),
              catchError(() => throwError(() => error)),
            );
        }

        tokenService.clearTokens();
        void router.navigate(['/login']);
      }

      return throwError(() => error);
    }),
  );
};
